"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MapLibreMap from "@/components/maplibre-map";
import {
  DEFAULT_PLACES_MAP_ZOOM,
  MAPLIBRE_PM_TILES_URL,
} from "@/components/maplibre-map/config";
import { getPlaceId } from "@/components/maplibre-map/places-adapter";
import { useAreaChips } from "@/components/maplibre-map/use-area-chips";
import { useAreaLandmarks } from "@/components/maplibre-map/use-area-landmarks";
import { useSubwayNetwork } from "@/components/maplibre-map/use-subway-network";
import Iconify from "@/components/ui/Iconify";
import { useTranslations } from "@/lib/static-intl";
import PlacesMobileBottomSheet from "../places-mobile-bottom-sheet";
import PlacesMobileTopControllers from "../places-mobile-map-controllers/places-mobile-top-controllers";
import PlacesListSearchBar from "../places-search-bar";
import PlacesCategoryChips from "../places-category-chips";
import { usePlaceViewNavigation } from "@/hooks/usePlaceViewNavigation";
import { getAllPlaceMediaByPlaceId } from "@/api/places";

const MobilePlaceMapMaplibre = ({
  loader,
  places,
  addQueryParam,
  mapCenter,
  mapZoom,
  mapMarkers,
  setMapCenter,
  setMapZoom,
  setMapBounds,
  fetchPlaces,
  setListPageNo,
  switchScreen,
  infiniteScrollLoader,
  searchAreaAgain,
  currentUserLocation,
  totalPlacesAvailable,
  openPlaceAddModal,
  handlePlaceSorting,
  searchQuery,
  setSearchQuery,
  handleSearchAroundMyLocation,
  completePlacesReset,
  placeFilters,
  setPlaceFilters,
  removeQueryParam,
}) => {
  const t = useTranslations("places.place_map");
  const { routeToPlaceDetail } = usePlaceViewNavigation();

  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [localCenter, setLocalCenter] = useState(mapCenter);
  const [localZoom, setLocalZoom] = useState(
    mapZoom || DEFAULT_PLACES_MAP_ZOOM
  );
  const [localBounds, setLocalBounds] = useState(null);
  const [tapRipple, setTapRipple] = useState(null);
  // Pin the user last opened (set by routeToPlaceDetail); re-highlighted when
  // they come back from the detail page.
  const [restoredSelectedId, setRestoredSelectedId] = useState(null);
  // Tapped-pin PREVIEW CARD (list-row tone). Tap a pin → this card shows the
  // place at the bottom; tap the card → detail. Lightweight: uses the data we
  // already have so it appears instantly, and only loads the heavy detail page
  // when the user commits by tapping the card.
  const [previewPlace, setPreviewPlace] = useState(null);
  const previewKeyRef = useRef(null);

  // auto-refresh-on-move state
  const lastFetchCenterRef = useRef(null);
  const lastFetchZoomRef = useRef(null);
  const refetchTimerRef = useRef(null);
  // 마지막으로 불러온 **범위**. 지금 보는 곳이 여기를 벗어나면 다시 부른다.
  const lastFetchBoundsRef = useRef(null); // debounce so only the SETTLED view refetches
  const skipNextIdleRef = useRef(true); // skip the initial idle (first load already fetches)
  const searchAreaAgainRef = useRef(searchAreaAgain);
  searchAreaAgainRef.current = searchAreaAgain;

  const distanceMeters = (a, b) => {
    if (!a || !b || a.lat == null || b.lat == null) return Infinity;
    const R = 6371000;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const s =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(s));
  };

  // On mount (incl. returning from a place detail), pick up the last-opened
  // place id so its pin comes back highlighted.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sid = window.sessionStorage?.getItem(
      "hotple:place-detail:selectedId"
    );
    if (sid) {
      setRestoredSelectedId(sid);
      window.sessionStorage?.removeItem("hotple:place-detail:selectedId");
    }
  }, []);

  // 분홍 테는 **사람이 고른 가게**에만 두른다.
  //
  // ⚠️ 예전에는 아무도 안 골랐을 때 목록의 **첫 줄**(= 지도 한가운데에서 가장 가까운
  //    가게)로 떨어졌다. 그래서 들어가자마자 누른 적 없는 가게에 테가 둘려 있었고,
  //    지도를 옮기면 다른 가게로 옮겨 갔으며, 끄는 방법이 없었다(2026-08-10 사장님 제보).
  //    옛 설계에서 시트가 옆으로 넘기는 카드 묶음이던 시절의 잔재다.
  //
  //    이제 테가 뜨는 경우는 셋뿐이고 셋 다 사람이 고른 것이다 — 핀을 눌렀을 때,
  //    목록의 줄을 눌렀을 때, 가게 화면에서 지도로 넘어왔을 때. 그리고 지울 수 있다.
  const selectedMarkerId = restoredSelectedId || null;

  // 넓게 볼 때 뜨는 이름표. 예전에는 `kr-subway-stations.json` 이라는 **코드에 박아 둔
  // 역 목록**이었다. 그래서 (1) 역이 없는 상권(연남·명물거리·중앙시장)은 아예 안 뜨고,
  // (2) 숫자가 999 에서 잘려 932곳도 5,000곳도 똑같아 보였고, (3) 어드민에서 사장님이
  // 확정한 상권 이름을 모바일만 못 썼다. 데스크톱은 이미 이 상권 칩을 쓰고 있었다.
  //
  // 지금은 **큰 이름 단계만**(`groupOnly`) 쓴다 — 세부 상권은 자동으로 붙은 이름이
  // 겹쳐서(홍대 일대에 "홍대" 3개) 고르기 어렵다. 어드민에서 이름을 확정하는 만큼
  // `groupOnly` 를 풀면 세부 단계도 그대로 켜진다.
  const { chips: areaChips, clickZoom: areaClickZoom } = useAreaChips({
    bounds: localBounds,
    zoom: localZoom,
    groupOnly: true,
  });

  // 칩이 사라지는 배율(15)부터 작은 이름표가 대신 뜬다 — "여기가 홍대 놀이터 앞" 을
  // 알려 주는 용도다. 누르는 것이 아니라 읽는 것이라 숫자도 없다.
  const landmarkLabels = useAreaLandmarks({
    bounds: localBounds,
    zoom: localZoom,
    minZoom: 15,
  });

  // 출구는 이름표보다 더 안쪽에서만. 넓게 보면 숫자밭이 된다.
  // 전국 지하철 한 벌(노선·역·출구). 화면 범위로 묻던 것을 파일 한 번 받기로 바꿨다 —
  // 지도를 움직여도 요청이 생기지 않는다.
  const subwayNetwork = useSubwayNetwork();

  const onCameraChanged = useCallback((camera) => {
    setLocalBounds(camera?.bounds);
    setLocalCenter(camera?.center);
    setLocalZoom(camera?.zoom);
  }, []);

  const onMapIdle = useCallback(
    (camera) => {
      const nextBounds = camera?.bounds || localBounds;
      const nextCenter = camera?.center || localCenter;
      const nextZoom = camera?.zoom || localZoom;

      setLocalBounds(nextBounds);
      setLocalCenter(nextCenter);
      setLocalZoom(nextZoom);
      setMapBounds(nextBounds);
      setMapCenter(nextCenter);
      setMapZoom(nextZoom);
      // NOTE: auto-refresh moved to onUserMoveEnd (fires the instant the finger
      // lifts, before the map fully idles) for a snappier 호갱노노-like feel.
      // 손 뗀 신호가 빠지는 경우를 대비해 여기서도 한 번 더 본다(위 설명 참고).
      maybeRefetchForView(camera);
    },
    [
      localBounds,
      localCenter,
      localZoom,
      setMapBounds,
      setMapCenter,
      setMapZoom,
    ]
  );

  // 지금 보는 곳이 이미 불러온 범위를 벗어났으면 다시 부른다.
  //
  // ⚠️ 예전에는 **손을 뗐을 때**(onUserMoveEnd)에서만 판단했다. 그런데 그 신호는
  //    "사용자가 직접 만진 것"으로 인식됐을 때만 온다 — 손가락으로 끄는 경우 그 인식이
  //    빠지면서 지도를 아무리 옮겨도 조회가 안 나갔다(2026-08-10 사장님 제보: 모바일에서
  //    핀이 안 뜨고 "이 지역 N곳" 이 그대로). 그래서 **지도가 멈췄을 때**(onIdle)에도
  //    같은 판단을 한 번 더 한다. 범위를 벗어나지 않았으면 아무 일도 하지 않으므로
  //    두 번 불려도 조회가 두 번 나가지 않는다.
  const maybeRefetchForView = useCallback((camera) => {
    const nextBounds = camera?.bounds;
    const nextCenter = camera?.center;
    const nextZoom = camera?.zoom;

    // 지금 보는 범위 중 **이미 불러온 범위가 덮지 못하는 부분**이 얼마나 되는지 잰다.
    //
    // ⚠️ 처음엔 "가장자리를 넘었나" 로 짰는데, 그러면 **범위가 똑같아도 참**이 되어
    //    한 번 끌 때마다 같은 조회가 스무 번씩 나갔다(2026-08-10 후보에서 잡았다).
    //    겹치는 넓이로 재야 맞다 — 같은 자리면 1.0, 완전히 벗어나면 0 이다.
    const uncovered = (() => {
      const seen = lastFetchBoundsRef.current;
      if (!seen || !nextBounds) return 1;
      const n = {
        north: Number(nextBounds.north),
        south: Number(nextBounds.south),
        east: Number(nextBounds.east),
        west: Number(nextBounds.west),
      };
      const area = (n.north - n.south) * (n.east - n.west);
      if (!(area > 0)) return 1;
      const ovLat = Math.max(0, Math.min(n.north, Number(seen.north)) - Math.max(n.south, Number(seen.south)));
      const ovLng = Math.max(0, Math.min(n.east, Number(seen.east)) - Math.max(n.west, Number(seen.west)));
      return 1 - (ovLat * ovLng) / area;
    })();
    // 화면의 5분의 1 넘게 새 땅이면 다시 부른다. 살짝 밀었을 때는 부르지 않는다.
    const leftFetchedArea = uncovered > 0.2;
    // 넓게 볼 때는 새로 드러난 주변도 채워야 한다. 확대할 때는 부르지 않는다 —
    // 이미 가진 것으로 충분하고, 중간 단계마다 부르면 핀이 깜빡인다.
    const zoomedOut =
      nextZoom != null &&
      lastFetchZoomRef.current != null &&
      nextZoom < lastFetchZoomRef.current - 0.8;
    if (!leftFetchedArea && !zoomedOut) return;

    if (nextCenter) lastFetchCenterRef.current = nextCenter;
    if (nextBounds) lastFetchBoundsRef.current = nextBounds;
    if (nextZoom != null) lastFetchZoomRef.current = nextZoom;
    // 빠르게 여러 번 움직여도 **멈춘 화면 한 번**만 부른다.
    if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
    refetchTimerRef.current = setTimeout(() => {
      searchAreaAgainRef.current?.();
    }, 150);
  }, []);

  // Fires the moment the user finishes dragging (finger lift) — much snappier
  // than waiting for the map's "idle" event. Only fires on user interaction.
  const onUserMoveEnd = useCallback(
    (camera) => {
      // Panning/zooming the map away dismisses the tapped-pin preview card.
      previewKeyRef.current = null;
      setPreviewPlace(null);
      const nextBounds = camera?.bounds;
      const nextCenter = camera?.center;
      const nextZoom = camera?.zoom;
      if (nextBounds) {
        setLocalBounds(nextBounds);
        setMapBounds(nextBounds);
      }
      if (nextCenter) {
        setLocalCenter(nextCenter);
        setMapCenter(nextCenter);
      }
      if (nextZoom) {
        setLocalZoom(nextZoom);
        setMapZoom(nextZoom);
      }

      if (skipNextIdleRef.current) skipNextIdleRef.current = false;
      maybeRefetchForView(camera);
    },
    [maybeRefetchForView, setMapBounds, setMapCenter, setMapZoom]
  );

  const handleMapMarkerClick = useCallback(
    (markerId, position, screenPoint) => {
      if (markerId == null || markerId === "") return;
      const key = String(markerId);
      let place = (places || []).find((p) =>
        [
          p?._id,
          p?.id,
          p?.placeUniqueNumber,
          p?.publicId,
          p?.contentid,
          p?.contentId,
        ].some((v) => v != null && String(v) === key)
      );
      // Map now plots up to 500 lightweight pins, but the list only hydrates 20.
      // For a tapped pin that isn't among the 20, build a minimal place from the
      // marker's own data (id = placeUniqueNumber, name) — enough for
      // buildPlaceDetailPath to route straight to the detail page.
      if (!place) {
        const marker = mapMarkers?.find((_m) => String(_m?.id) === key);
        if (marker) {
          const c = marker?.geometry?.coordinates;
          place = {
            placeUniqueNumber: key,
            name: marker?.properties?.name,
            type: marker?.properties?.type,
            naverRating: marker?.properties?.naverRating,
            numberOfVisitorReviews: marker?.properties?.numberOfVisitorReviews,
            latitude: Array.isArray(c) ? c[1] : position?.lat,
            longitude: Array.isArray(c) ? c[0] : position?.lng,
            source: "api",
          };
        }
      }
      if (!place) return;

      // Tap feedback: ripple at the tap point + highlight the tapped pin.
      if (screenPoint) {
        setTapRipple({ x: screenPoint.x, y: screenPoint.y, k: Date.now() });
      }
      setRestoredSelectedId(key);

      // Show the preview card INSTANTLY with the data we already have.
      let distance = place.distance;
      if (
        currentUserLocation &&
        place.latitude != null &&
        place.longitude != null
      ) {
        distance = distanceMeters(
          { lat: Number(place.latitude), lng: Number(place.longitude) },
          {
            lat: currentUserLocation.latitude,
            lng: currentUserLocation.longitude,
          }
        );
      }
      previewKeyRef.current = key;
      setPreviewPlace({
        ...place,
        distance,
        distanceFromMe: distance != null ? distance : place.distanceFromMe,
      });

      // Pins beyond the hydrated 20 arrive with no photo — fetch this one place's
      // summary so the card fills its thumbnail a beat later. Guarded so a stale
      // response can't overwrite a newer selection, and merged only if it looks
      // like a real place (has a name).
      const hasMedia =
        (Array.isArray(place.placeMedia) && place.placeMedia.length > 0) ||
        place.firstimage ||
        place.thumbnailPath;
      if (!hasMedia) {
        // The tapped pin isn't among the hydrated 20, so it has no photo yet.
        // Fetch just this place's media — /places/:id/media returns the records
        // WITH thumbnails (unlike /:id/with-reviews, which returns an EMPTY
        // placeMedia array — that was the bug: far-pin cards stayed blank even
        // though the place had photos). Guarded against a stale/racey response.
        getAllPlaceMediaByPlaceId(key)
          .then((data) => {
            const media = Array.isArray(data)
              ? data
              : data?.media || data?.data || [];
            if (media.length && previewKeyRef.current === key) {
              setPreviewPlace((prev) =>
                prev ? { ...prev, placeMedia: media } : prev
              );
            }
          })
          .catch(() => {});
      }
    },
    [places, mapMarkers, currentUserLocation]
  );

  const closePreview = useCallback(() => {
    previewKeyRef.current = null;
    setPreviewPlace(null);
  }, []);

  // 고른 것을 지운다 — 테와 미리보기 카드를 함께 거둔다.
  const clearSelection = useCallback(() => {
    setRestoredSelectedId(null);
    previewKeyRef.current = null;
    setPreviewPlace(null);
  }, []);

  const handleMarkerClick = useCallback(
    (marker, position, screenPoint) => {
      handleMapMarkerClick(getPlaceId(marker), position, screenPoint);
    },
    [handleMapMarkerClick]
  );

  const showMyLocation = (loc) => {
    if (loc?.latitude && loc?.longitude) {
      const nextCenter = {
        lat: loc.latitude,
        lng: loc.longitude,
      };
      skipNextIdleRef.current = true;
      setMapCenter(nextCenter);
      setLocalCenter(nextCenter);
    }
  };

  // Zoom +/- buttons (rendered in the bottom-sheet floating stack). Driving
  // localZoom re-drives MapLibreMap's zoom prop, which eases the camera — same
  // path a gesture zoom takes, so no direct map-ref plumbing is needed.
  const zoomBy = (delta) =>
    setLocalZoom((z) => {
      const cur = Number(z || mapZoom || DEFAULT_PLACES_MAP_ZOOM);
      return Math.min(20, Math.max(3, cur + delta));
    });

  // Keep the map camera in the URL via replaceState (NOT addQueryParam, which
  // pushState-s a new history entry on every pan). Each pan used to push an
  // untracked entry, desyncing Next's App-Router history — so after opening a
  // place and pressing Back, the router couldn't restore the /places view at
  // the last camera position. replaceState updates the URL in place, and we
  // PRESERVE window.history.state so Next's own router state stays intact.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const lat = mapCenter?.lat;
    const lng = mapCenter?.lng;
    if (!lat || !lng || !mapZoom) return;
    const params = new URLSearchParams(window.location.search);
    params.set("mc", `${lat},${lng}`);
    params.set("mz", String(mapZoom));
    const nextUrl = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
    const curUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (nextUrl !== curUrl) {
      window.history.replaceState(window.history.state, "", nextUrl);
    }
  }, [mapCenter, mapZoom]);

  useEffect(() => {
    if (mapCenter) setLocalCenter(mapCenter);
  }, [mapCenter]);

  useEffect(() => {
    if (mapZoom) setLocalZoom(mapZoom);
  }, [mapZoom]);

  useEffect(() => {
    if (mapCenter) setIsMapLoaded(true);
  }, [mapCenter]);

  useEffect(() => {
    if (isMapLoaded && places?.length === 0) fetchPlaces();
  }, [fetchPlaces, isMapLoaded, places?.length]);

  return (
    <div
      data-maplibre-mobile-place-map
      className="relative h-full max-h-full w-full overflow-hidden"
    >
      <style>{`
        @keyframes hotplePinTap {
          0% { transform: translate(-50%, -50%) scale(0.3); opacity: 0.55; }
          100% { transform: translate(-50%, -50%) scale(3.6); opacity: 0; }
        }
      `}</style>
      {tapRipple && (
        <span
          key={tapRipple.k}
          onAnimationEnd={() => setTapRipple(null)}
          style={{
            position: "absolute",
            left: tapRipple.x,
            top: tapRipple.y,
            width: 34,
            height: 34,
            borderRadius: 9999,
            background: "rgba(229,19,111,0.35)",
            border: "2px solid rgba(229,19,111,0.7)",
            pointerEvents: "none",
            zIndex: 25,
            animation: "hotplePinTap 0.5s ease-out forwards",
          }}
        />
      )}
      {mapCenter ? (
        <MapLibreMap
          pmtilesUrl={MAPLIBRE_PM_TILES_URL}
          markers={mapMarkers}
          mapCenter={localCenter || mapCenter}
          mapZoom={localZoom || mapZoom || DEFAULT_PLACES_MAP_ZOOM}
          showNavigationControl={false}
          areaLabels={areaChips}
          areaLabelZoomMax={15}
          landmarkLabels={landmarkLabels}
          subwayNetwork={subwayNetwork}
          areaLabelClickZoom={areaClickZoom}
          selectedMarkerId={selectedMarkerId}
          onCameraChange={onCameraChanged}
          onIdle={onMapIdle}
          onUserMoveEnd={onUserMoveEnd}
          onMarkerClick={handleMarkerClick}
        />
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center overflow-hidden bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-300">
          <Iconify
            icon="teenyicons:loader-outline"
            className="!h-8 !min-w-8 !w-8 animate-spin"
          />
          <span className="mt-2 text-center text-lg font-medium">
            {t("map_prep_text")}
          </span>
          <span className="max-w-xs text-center text-sm text-gray-600 dark:text-gray-400">
            {t("map_prep_subtext")}
          </span>
        </div>
      )}

      {mapCenter && (
        <div className="absolute top-0 left-0 right-0 z-20 flex flex-col gap-2 px-3 pt-3 pointer-events-none">
          <div className="pointer-events-auto">
            <PlacesListSearchBar
              floating
              addQueryParam={addQueryParam}
              removeQueryParam={removeQueryParam}
              currentUserLocation={currentUserLocation}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              switchScreenToInitial={() => {}}
              handleSearchAroundMyLocation={handleSearchAroundMyLocation}
              completePlacesReset={completePlacesReset}
            />
            <PlacesCategoryChips
              placeFilters={placeFilters}
              setPlaceFilters={setPlaceFilters}
            />
          </div>
          <div className="pointer-events-auto">
            <PlacesMobileTopControllers
              mapMarkers={mapMarkers}
              setListPageNo={setListPageNo}
              searchAreaAgain={() => {
                        closePreview();
                searchAreaAgain();
              }}
              infiniteScrollLoader={infiniteScrollLoader}
              addQueryParam={addQueryParam}
              totalPlacesAvailable={totalPlacesAvailable}
            />
          </div>
        </div>
      )}

      {mapCenter && (
        <PlacesMobileBottomSheet
          places={places}
          loader={loader}
          infiniteScrollLoader={infiniteScrollLoader}
          totalPlacesAvailable={totalPlacesAvailable}
          setListPageNo={setListPageNo}
          handlePlaceSorting={handlePlaceSorting}
          openPlaceAddModal={openPlaceAddModal}
          showMyLocation={showMyLocation}
          onZoomIn={() => zoomBy(1)}
          onZoomOut={() => zoomBy(-1)}
          currentUserLocation={currentUserLocation}
          previewPlace={previewPlace}
          onPreviewClose={closePreview}
          hasSelection={Boolean(selectedMarkerId)}
          onClearSelection={clearSelection}
          onRowSelect={(place) => {
            // Highlight the tapped list row's pin on the map (same as tapping a
            // pin). The map stays mounted behind the detail overlay, so this
            // selection persists after closing it.
            const sid =
              place?.placeUniqueNumber ??
              place?.contentid ??
              place?.contentId ??
              place?.publicId ??
              place?._id ??
              place?.id;
            if (sid != null) setRestoredSelectedId(String(sid));
          }}
        />
      )}

    </div>
  );
};

export default React.memo(MobilePlaceMapMaplibre);
